import type { PlatformStatus } from './components/StatusBadge';

// The live API explorer. For now this is the sb-api landing SPA served at the
// api host root.
// TODO: flip to internal /explorer route after the fold (see explorer-fold spec)
export const EXPLORER_URL = 'https://api.studiobereikbaar.nl/';

/** Self-service tools launchpad (tools.studiobereikbaar.nl). */
export const TOOLS_URL = '/tools';

/** Self-hosted MapHub. */
export const MAPHUB_URL = 'https://maphub.studiobereikbaar.nl';

/**
 * Capability status is AUTHORED roadmap judgment (Live / Building / Exploring),
 * NOT registry state — so it is hardcoded here. Counts that DO live in the
 * registry are pulled dynamically via useCatalog and injected at render, keyed
 * by `dynamic`. Keep this list honest: "Building" means not-yet-done.
 */
export interface Capability {
  label: string;
  status: PlatformStatus;
  /** Extra honest context rendered under the label. */
  note?: string;
  /** When set, a live count from the registry is appended to the note. */
  dynamic?: 'processes' | 'domains';
}

export const liveCapabilities: Capability[] = [
  {
    label: 'Registry backbone',
    status: 'live',
    note: 'One source of truth for where every dataset physically lives.',
  },
  {
    label: 'OGC API processes',
    status: 'live',
    note: 'Standardised analysis endpoints',
    dynamic: 'processes',
  },
  { label: 'Self-service tools', status: 'live', note: 'Answer common questions at tools.sb.nl.' },
  {
    label: 'Datasets across domains',
    status: 'live',
    note: 'Tracked in the registry',
    dynamic: 'domains',
  },
  { label: 'MapHub self-hosted', status: 'live', note: 'Browse maps in-browser or sync via QGIS.' },
  {
    label: 'Claude connectors',
    status: 'live',
    note: 'Mobility + knowledge connectors for the team.',
  },
  { label: 'Live API explorer', status: 'live', note: 'Try every method against real data.' },
  { label: 'Whole stack authenticated', status: 'live', note: 'Single sign-on across the platform.' },
];

export const buildingCapabilities: Capability[] = [
  {
    label: 'Define once → every surface auto-generates',
    status: 'building',
    note: 'Today a subset of methods are hand-wired as Claude tools. The generator that grows this automatically is being built — it is not done yet.',
  },
  {
    label: 'MapHub as a zero-copy door',
    status: 'building',
    note: 'Publish OGC process results to MapHub without duplicating the bytes.',
  },
  {
    label: 'The /kaarten map library',
    status: 'building',
    note: 'A browsable library of finished maps.',
  },
];

export const exploringCapabilities: Capability[] = [
  { label: 'Selling data & methods externally', status: 'exploring' },
  { label: 'Team private-network migration', status: 'exploring' },
  { label: 'Organisation-wide Claude usage insights', status: 'exploring' },
];

/**
 * Resolve a capability's dynamic count into a display string. Returns the
 * honest "live count unavailable" phrasing when the registry could not be
 * reached (never a hardcoded fallback number).
 */
export function resolveCapabilityNote(
  capability: Capability,
  counts: { processCount: number | null; domainCount: number | null },
): string | undefined {
  if (!capability.dynamic) return capability.note;
  const value =
    capability.dynamic === 'processes' ? counts.processCount : counts.domainCount;
  const suffix = value === null ? 'live count unavailable' : `${value} live`;
  return capability.note ? `${capability.note} · ${suffix}` : suffix;
}
